import { Button, Skeleton } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { Link } from "@tanstack/react-router";
import { ArrowRight, Monitor, Music, Play, RotateCcw } from "lucide-react";
import { useCallback, useMemo, useState } from "react";

import type { Asset, AssetKind, AssetOrder } from "../../../api/generated/model";
import { ErrorAlert } from "../../../platform/ErrorAlert";
import { AssetMenu } from "../../../platform/media/AssetMenu";
import { useAssetDelete } from "../../../platform/media/asset-delete";
import { shapeRatio } from "../../../platform/media/asset-shape";
import { CreatorFilter } from "../../../platform/media/CreatorFilter";
import { LIBRARY_ORDERS, LIBRARY_SEARCH_MAX, useLibraryPages } from "../../../platform/media/library-pages";
import { ProductFilter } from "../../../platform/media/ProductFilter";
import { useMembership } from "../../../platform/membership";
import { canEditContent } from "../../../platform/permissions";
import { EmptyState } from "../../../ui/EmptyState";
import { FilterMenu } from "../../../ui/FilterMenu";
import { LoadAhead, morePages } from "../../../ui/LoadAhead";
import { SortMenu } from "../../../ui/SortMenu";
import { SubmitSearch } from "../../../ui/SubmitSearch";
import { ThumbImage } from "../../../ui/ThumbImage";
import { VirtualMasonry, type GridCardProps } from "../../../ui/VirtualMasonry";
import home from "../pro-home.module.css";
import classes from "./uploads.module.css";

/** The Library's own files inside Pro, where a file's preview opens (spec ui-switch, e3). */
const FILES = "/teams/$teamId/brands/$brandId/pro/library/files";
/** The Library's page of files (live's 48). */
const PAGE = 48;
/** Live's Media filter (`mediaTypeOptions`), as Classic's Library. */
const MEDIA = [
  { value: "image", label: "Image" },
  { value: "video", label: "Video" },
] as const satisfies readonly { value: AssetKind; label: string }[];

interface CardData {
  teamId: string;
  brandId: string;
  mayChange: (asset: Asset) => boolean;
  onDelete: (asset: Asset) => void;
  onLost: () => void;
}

/** A file's picture: an image's thumbnail, a video's still, a sound's icon. */
function Picture({ asset, onLost }: { asset: Asset; onLost: () => void }) {
  const still = asset.kind === "video" ? asset.urls.poster : asset.kind === "image" ? asset.urls.original : null;
  if (still === null) {
    const Icon = asset.kind === "audio" ? Music : Monitor;
    return <Icon size={24} strokeWidth={1.5} className={home.mediaIcon} aria-hidden />;
  }
  return <ThumbImage thumb={asset.urls.thumb} full={still} alt="" onLost={onLost} />;
}

/** One upload: its picture opens its preview in the Library's files; its menu is the Library's. */
function UploadCard({ item: asset, data }: GridCardProps<Asset, CardData>) {
  return (
    <div className={classes.card}>
      <Link
        to={FILES}
        params={{ teamId: data.teamId, brandId: data.brandId }}
        search={{ preview: asset.id }}
        className={classes.media}
        aria-label={asset.name}
        title={asset.name}
      >
        <Picture asset={asset} onLost={data.onLost} />
        {asset.kind === "video" ? <Play size={16} className={classes.play} fill="currentColor" aria-hidden /> : null}
        <span className={classes.name}>{asset.name}</span>
      </Link>
      <div className={classes.menu}>
        <AssetMenu
          teamId={data.teamId}
          brandId={data.brandId}
          asset={asset}
          mayChange={data.mayChange(asset)}
          onDelete={data.onDelete}
        />
      </div>
    </div>
  );
}

const keyOf = (asset: Asset) => asset.id;
const ratioOf = (asset: Asset) => (asset.kind === "audio" ? 1 : shapeRatio(asset));

export interface UploadsTabProps {
  teamId: string;
  brandId: string;
}

/**
 * Pro's Library > Uploads (spec ui-perf/pro-library-tabs): the brand's Manual Uploads as live's
 * tab of them, with the Library's own pieces: search on Enter, creator chips, Product and Media,
 * sort, each card's menu, and only the cards near the view mounted, so a brand with thousands of
 * uploads scrolls to its end (`VirtualMasonry` in Pro's own scroller, `#main`).
 */
export function UploadsTab({ teamId, brandId }: UploadsTabProps) {
  const [search, setSearch] = useState("");
  const [createdBy, setCreatedBy] = useState<string[]>([]);
  const [productId, setProductId] = useState<string | undefined>(undefined);
  const [kind, setKind] = useState<AssetKind | undefined>(undefined);
  const [order, setOrder] = useState<AssetOrder>("newest");
  const files = useLibraryPages(
    teamId,
    brandId,
    {
      trash: false,
      source: ["upload"],
      ...(search === "" ? {} : { search }),
      ...(createdBy.length === 0 ? {} : { createdBy }),
      productId,
      kind,
      order,
    },
    true,
    { size: PAGE },
  );
  const items = useMemo(() => files.data?.pages.flatMap((each) => each.items), [files.data]);
  // The grid follows the frame's own scroller, which the page sits in.
  const [root, setRoot] = useState<HTMLElement | null>(null);
  const attach = useCallback((element: HTMLDivElement | null) => setRoot(element?.closest("main") ?? null), []);
  const phone = useMediaQuery("(max-width: 48em)") === true;
  const me = useMembership(teamId);
  const deletion = useAssetDelete(teamId, brandId);
  const { refetch } = files;
  const data = useMemo<CardData>(
    () => ({
      teamId,
      brandId,
      mayChange: (asset) => me !== undefined && canEditContent(me.role, asset.created_by ?? "", me.userId),
      onDelete: deletion.ask,
      onLost: () => void refetch(),
    }),
    [teamId, brandId, me, deletion.ask, refetch],
  );
  const narrowed = search !== "" || createdBy.length > 0 || productId !== undefined || kind !== undefined;
  return (
    <div ref={attach}>
      <div className={classes.toolbar}>
        <SubmitSearch
          label="Search uploads"
          value={search}
          maxLength={LIBRARY_SEARCH_MAX}
          onSearch={(typed) => setSearch(typed.trim())}
        />
        <CreatorFilter teamId={teamId} value={createdBy} onChange={setCreatedBy} />
        <ProductFilter teamId={teamId} value={productId} onChange={setProductId} />
        <FilterMenu
          label="Media"
          icon={<Monitor size={16} />}
          options={MEDIA}
          value={kind}
          onChange={(next) => setKind(next)}
        />
        {productId !== undefined || kind !== undefined ? (
          <Button
            variant="text"
            size="compact-sm"
            leftSection={<RotateCcw size={16} />}
            onClick={() => {
              setProductId(undefined);
              setKind(undefined);
            }}
          >
            Reset
          </Button>
        ) : null}
        <span className={classes.grow} />
        <SortMenu options={LIBRARY_ORDERS} value={order} onChange={setOrder} />
        <Link
          to={FILES}
          params={{ teamId, brandId }}
          search={{ source: "upload" }}
          className={`${home.subtle} ${classes.upload}`}
        >
          Upload in the Library <ArrowRight size={14} aria-hidden />
        </Link>
      </div>
      {files.isError ? <ErrorAlert error={files.error} /> : null}
      {items === undefined ? (
        <div className={classes.skeletons} aria-label="Loading uploads">
          {[0, 1, 2, 3].map((each) => (
            <Skeleton key={each} className={home.mediaSkeleton} />
          ))}
        </div>
      ) : items.length === 0 ? (
        narrowed ? (
          <EmptyState
            title="No uploads found"
            description="Check for typos or filters, or try a different search query."
          />
        ) : (
          <EmptyState title="No uploads yet" description="Files you upload show up here." />
        )
      ) : (
        <>
          <VirtualMasonry
            items={items}
            keyOf={keyOf}
            ratioOf={ratioOf}
            card={UploadCard}
            data={data}
            columns={phone ? 2 : 5}
            root={root}
            gap={12}
            label="Uploads"
          />
          <LoadAhead more={morePages(files)} loaded={items.length} root={root} ahead={3} label="Loading more uploads" />
        </>
      )}
      {deletion.dialog}
    </div>
  );
}
